import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import axiosInstance from '../../api/axiosInstance'

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
  purple: '#7C3AED',
}

const ACTION_META = {
  POST:   { label: 'CREATE', bg: '#dcfce7', color: '#166534' },
  PUT:    { label: 'EDIT',   bg: '#fef9c3', color: '#854d0e' },
  PATCH:  { label: 'EDIT',   bg: '#fef9c3', color: '#854d0e' },
  DELETE: { label: 'DELETE', bg: '#fee2e2', color: '#991b1b' },
}

// Mirrors MODULE_MAP in Medicare/src/middlewares/auditMiddleware.js.
// Grouped so the sensitive areas are easy to reach from the dropdown.
const MODULES = [
  // identity & access
  'Auth', 'UserAccount', 'Doctor',
  // money
  'Invoice', 'Payment', 'FinanceConfig', 'InsuranceContract',
  'Insurance', 'InsuranceCard',
  // patient record
  'Patient', 'Appointment', 'Scheduling', 'Prescription', 'Vitals',
  'MedicalData', 'Diagnosis', 'LabRequest', 'Pharmacy', 'PatientServices',
  'XRay', 'DentalChart', 'DermaChart', 'ChronicIllness', 'PatientAllergy',
  'NurseNote', 'SickLeave', 'Consent',
  // forms
  'Form-Acknowledgement', 'Form-MedicalCert', 'Form-Referral',
  'Form-HealthCert', 'Form-HousemaidCert', 'Document',
  // outbound communication
  'Messaging', 'MessagingConfig',
  // catalogues & configuration
  'Drug', 'Services', 'ServiceCategory', 'Package', 'LabConfig',
  'Speciality', 'Category', 'Complaint', 'AllergyMaster',
  'UploadTypeMaster', 'DiagnosisMaster', 'Template', 'Assistant', 'Inventory',
  // system
  'SystemSettings', 'Integration', 'DataMigration', 'DatabaseMaintenance',
  'Cron', 'Other',
]

const SEVERITY_META = {
  critical: { label: 'Critical', bg: '#fee2e2', color: '#991b1b' },
  warning:  { label: 'Warning',  bg: '#fef3c7', color: '#92400e' },
  info:     { label: 'Info',     bg: '#e8ecf0', color: '#5B6B79' },
}

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 12px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 12, color: '#2d3748', padding: '7px 12px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

const Badge = ({ label, bg, color }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
    background: bg || '#e8ecf0', color: color || COLORS.gray, whiteSpace: 'nowrap',
  }}>{label}</span>
)

const FilterInput = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </label>
    {children}
  </div>
)

const inputStyle = {
  height: 32, borderRadius: 7, border: '1px solid #dde3eb',
  padding: '0 10px', fontSize: 12, color: '#2d3748', outline: 'none',
  background: '#fff',
}

// Same check SettingNew.jsx uses to gate the Database Cleanup tile. The server
// enforces this too — this only decides whether the button is worth showing.
const isAdminUser = () => {
  try {
    const raw = localStorage.getItem('userMedical')
    return !!raw && String(JSON.parse(raw)?.data?.role || '').toLowerCase() === 'admin'
  } catch {
    return false
  }
}

// Keys are "<scope>" or "<scope>:<months>", matching what the purge endpoint
// accepts. Chat dock traffic is no longer recorded, so the last option is a
// one-time sweep of what was logged before that stopped — it has no age limit.
const RETENTION_OPTIONS = {
  'age:3': 'Older than 3 months',
  'age:6': 'Older than 6 months',
  'chat':  'Chat & AI assistant entries (any age)',
}

const paginBtn = (disabled) => ({
  width: 28, height: 28, borderRadius: 6, border: `1px solid ${COLORS.blue}`,
  background: disabled ? '#f5f7fa' : COLORS.blue, color: disabled ? '#aaa' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center', fontSize: 13,
})

const AuditLog = () => {
  const navigate = useNavigate()
  const [logs,       setLogs]       = useState([])
  const [loading,    setLoading]    = useState(false)
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 })

  const [purging,   setPurging]   = useState(false)
  const [isAdmin]                 = useState(isAdminUser)

  const [filters, setFilters] = useState({
    module: '', action: '', severity: '', date_from: '', date_to: '', search: '',
  })

  // `override` lets Reset fetch with cleared filters immediately: reading
  // `filters` here would see the pre-reset values from this render's closure.
  const fetchLogs = useCallback(async (page = 1, override = null) => {
    const f = override || filters
    setLoading(true)
    try {
      const params = { page, limit: pagination.limit }
      if (f.module)    params.module    = f.module
      if (f.action)    params.action    = f.action
      if (f.severity)  params.severity  = f.severity
      if (f.date_from) params.date_from = f.date_from
      if (f.date_to)   params.date_to   = f.date_to
      if (f.search)    params.search    = f.search
      const res = await axiosInstance.get('getAuditLogs', { params })
      if (res.data?.success) {
        setLogs(res.data.data || [])
        setPagination(prev => ({ ...prev, ...res.data.pagination }))
      }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [filters, pagination.limit])

  useEffect(() => { fetchLogs(1) }, []) // eslint-disable-line

  const handleSearch = (e) => { e.preventDefault(); fetchLogs(1) }
  const handleReset  = () => {
    const cleared = { module: '', action: '', severity: '', date_from: '', date_to: '', search: '' }
    setFilters(cleared)
    fetchLogs(1, cleared)
  }

  const setF = (key, val) => setFilters(f => ({ ...f, [key]: val }))

  const formatDate = (dt) => {
    if (!dt) return '—'
    return new Date(dt).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  }

  // Pick an age, see exactly how many rows that covers, then confirm. The dry
  // run matters: the cutoff is relative, so the count is only knowable server
  // side and deleting audit history is not reversible.
  const handlePurge = async () => {
    const { value: choice } = await Swal.fire({
      title: 'Clean up old logs',
      text: 'Permanently delete audit entries past a certain age.',
      icon: 'question',
      input: 'select',
      inputOptions: RETENTION_OPTIONS,
      inputPlaceholder: 'Select an age',
      showCancelButton: true,
      confirmButtonText: 'Continue',
      inputValidator: v => (!v ? 'Please choose an age' : undefined),
    })
    if (!choice) return

    const [scope, rawMonths] = choice.split(':')
    const months = rawMonths ? parseInt(rawMonths, 10) : undefined

    setPurging(true)
    try {
      const preview = await axiosInstance.post('auditLog/purge', { scope, months, dryRun: true })
      if (!preview.data?.success) {
        Swal.fire('Error', preview.data?.message || 'Could not check the log', 'error')
        return
      }

      const { rows, cutoff } = preview.data
      if (!rows) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to clean up',
          text: scope === 'chat'
            ? 'There are no chat or AI assistant entries left in the log.'
            : `No entries are older than ${months} months.`,
        })
        return
      }

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `This will permanently delete ${rows.toLocaleString()} log `
            + `entr${rows === 1 ? 'y' : 'ies'} `
            + (scope === 'chat'
                ? 'from the chat dock (staff chat, AI agent and assistant). '
                : `recorded before ${formatDate(cutoff)}. `)
            + `You won't be able to revert this!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete them!',
      })
      if (!result.isConfirmed) return

      const res = await axiosInstance.post('auditLog/purge', { scope, months, dryRun: false })
      if (res.data?.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Cleaned up',
          text: res.data.message,
          timer: 1800,
          showConfirmButton: false,
        })
        fetchLogs(1)
      } else {
        Swal.fire('Error', res.data?.message || 'Cleanup failed', 'error')
      }
    } catch (e) {
      Swal.fire('Error', e.response?.data?.message || 'Cleanup failed', 'error')
    } finally {
      setPurging(false)
    }
  }

  const statusMeta = (code) => {
    if (code >= 200 && code < 300) return { bg: '#dcfce7', color: '#166534' }
    if (code >= 400)               return { bg: '#fee2e2', color: '#991b1b' }
    return { bg: '#e8ecf0', color: COLORS.gray }
  }

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, marginRight: 4, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.purple}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-history" style={{ color: COLORS.purple, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Audit Log</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Track all system activity — creates, edits and deletions</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Badge label={`${pagination.total} records`} bg={`${COLORS.purple}15`} color={COLORS.purple} />
              {isAdmin && (
                <button onClick={handlePurge} disabled={purging}
                  title="Delete audit entries by age, or clear leftover chat traffic"
                  style={{
                    height: 32, padding: '0 12px', fontSize: 12, fontWeight: 600, borderRadius: 7,
                    border: 'none', background: purging ? '#cbd5e0' : COLORS.red, color: '#fff',
                    cursor: purging ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  <i className="ti ti-trash" style={{ fontSize: 14 }} />
                  {purging ? 'Cleaning…' : 'Clean up old logs'}
                </button>
              )}
            </div>
          </div>

          {/* ── Filter Bar ── */}
          <form onSubmit={handleSearch}>
            <div style={{
              padding: '10px 20px', borderBottom: '1px solid #e8ecf0',
              background: '#fafbfc', display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap',
            }}>

              <FilterInput label="Search">
                <div style={{ position: 'relative' }}>
                  <i className="ti ti-search" style={{
                    position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                    color: COLORS.gray, fontSize: 13, pointerEvents: 'none',
                  }} />
                  <input
                    type="text"
                    placeholder="Endpoint, user…"
                    value={filters.search}
                    onChange={e => setF('search', e.target.value)}
                    style={{ ...inputStyle, paddingLeft: 28, width: 200 }}
                  />
                </div>
              </FilterInput>

              <FilterInput label="Module">
                <select value={filters.module} onChange={e => setF('module', e.target.value)}
                  style={{ ...inputStyle, width: 150 }}>
                  <option value="">All Modules</option>
                  {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </FilterInput>

              <FilterInput label="Action">
                <select value={filters.action} onChange={e => setF('action', e.target.value)}
                  style={{ ...inputStyle, width: 120 }}>
                  <option value="">All Actions</option>
                  <option value="POST">CREATE</option>
                  <option value="PUT">EDIT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="SYSTEM">SYSTEM</option>
                </select>
              </FilterInput>

              <FilterInput label="Severity">
                <select value={filters.severity} onChange={e => setF('severity', e.target.value)}
                  style={{ ...inputStyle, width: 120 }}>
                  <option value="">All Levels</option>
                  <option value="critical">Critical</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </select>
              </FilterInput>

              <FilterInput label="From">
                <input type="date" value={filters.date_from}
                  onChange={e => setF('date_from', e.target.value)}
                  style={{ ...inputStyle, width: 140 }} />
              </FilterInput>

              <FilterInput label="To">
                <input type="date" value={filters.date_to}
                  onChange={e => setF('date_to', e.target.value)}
                  style={{ ...inputStyle, width: 140 }} />
              </FilterInput>

              <div style={{ display: 'flex', gap: 6, marginBottom: 1 }}>
                <button type="submit"
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ fontSize: 12, padding: '5px 12px', height: 32 }}>
                  <i className="ti ti-search" style={{ fontSize: 13 }} /> Search
                </button>
                <button type="button"
                  onClick={handleReset}
                  style={{
                    height: 32, padding: '0 12px', fontSize: 12, borderRadius: 7,
                    border: '1px solid #dde3eb', background: '#f8f9fb',
                    color: COLORS.gray, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                  <i className="ti ti-refresh" style={{ fontSize: 13 }} /> Reset
                </button>
              </div>

            </div>
          </form>

          {/* ── Table ── */}
          <div style={{ padding: '14px 20px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: COLORS.gray }}>
                <div className="spinner-border" role="status"
                  style={{ color: COLORS.purple, width: 28, height: 28, borderWidth: 3 }} />
                <div style={{ marginTop: 10, fontSize: 13 }}>Loading logs…</div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.purple}` }}>#</th>
                      <th style={thStyle}>Date & Time</th>
                      <th style={thStyle}>Level</th>
                      <th style={thStyle}>User</th>
                      <th style={thStyle}>Role</th>
                      <th style={thStyle}>Module</th>
                      <th style={thStyle}>Action</th>
                      <th style={thStyle}>Endpoint</th>
                      <th style={thStyle}>Patient</th>
                      <th style={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ ...tdStyle, textAlign: 'center', padding: '36px 0', color: COLORS.gray }}>
                          <i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                          No records found
                        </td>
                      </tr>
                    ) : logs.map((log, i) => {
                      const action = ACTION_META[log.action] || { label: log.action, bg: '#e8ecf0', color: COLORS.gray }
                      const status = statusMeta(log.status_code)
                      const sev    = SEVERITY_META[log.severity] || SEVERITY_META.info
                      const rowBg  = i % 2 === 0 ? '#fff' : '#fafbfc'
                      return (
                        <tr key={log.id}
                          style={{ background: rowBg }}
                          onMouseEnter={e => e.currentTarget.style.background = `${COLORS.purple}08`}
                          onMouseLeave={e => e.currentTarget.style.background = rowBg}
                        >
                          <td style={{ ...tdStyle, color: COLORS.gray, fontSize: 11 }}>
                            {(pagination.page - 1) * pagination.limit + i + 1}
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            {formatDate(log.created_at)}
                            {/* Repeats of the same action collapse into one row;
                                the badge is how many times it actually happened. */}
                            {log.repeat_count > 1 && (
                              <span style={{ marginLeft: 6 }}
                                title={`Repeated ${log.repeat_count} times — last at ${formatDate(log.last_seen_at)}`}>
                                <Badge label={`×${log.repeat_count}`}
                                  bg={`${COLORS.orange}22`} color="#8a4b00" />
                              </span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <Badge label={sev.label} bg={sev.bg} color={sev.color} />
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 600 }}>{log.user_name || '—'}</div>
                            {log.ip_address && (
                              <div style={{ fontSize: 10, color: COLORS.gray }}>{log.ip_address}</div>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <Badge label={log.user_role || '—'} bg="#e8ecf0" color={COLORS.gray} />
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: 600, color: COLORS.purple, fontSize: 12 }}>
                              {log.module || '—'}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <Badge label={action.label} bg={action.bg} color={action.color} />
                          </td>
                          <td style={tdStyle}>
                            <code style={{ fontSize: 11, color: '#555', background: '#f4f4f8', padding: '1px 5px', borderRadius: 4 }}>
                              {log.endpoint}
                            </code>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {log.patient_id
                              ? <Badge label={`#${log.patient_id}`} bg={`${COLORS.blue}33`} color="#1565c0" />
                              : <span style={{ color: '#ccc' }}>—</span>}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <Badge label={log.status_code} bg={status.bg} color={status.color} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Pagination ── */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                <span style={{ fontSize: 12, color: COLORS.gray }}>
                  Page <strong>{pagination.page}</strong> of <strong>{pagination.pages}</strong>
                  &nbsp;·&nbsp; {pagination.total} total records
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => fetchLogs(pagination.page - 1)}
                    style={paginBtn(pagination.page <= 1)}
                  >
                    <i className="ti ti-chevron-left" />
                  </button>
                  <span style={{ fontSize: 12, color: COLORS.gray, fontWeight: 600 }}>
                    {pagination.page} / {pagination.pages}
                  </span>
                  <button
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => fetchLogs(pagination.page + 1)}
                    style={paginBtn(pagination.page >= pagination.pages)}
                  >
                    <i className="ti ti-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}

export default AuditLog
